import React from 'react';
import { CloudServerOutlined, DatabaseOutlined, DollarOutlined, SafetyOutlined } from '@ant-design/icons';
import { ExecutivePresentation, InventoryRow, SummaryMetric, TabSection } from './types';
import {
  buildSecurityRiskItems,
  buildSecurityRiskSubtitle,
  buildServices,
  normalizeStatusTone,
  numberFormatter,
  resolveCustomerLabel,
  toArray
} from './shared';

const buildSummaryMetrics = (data: any): SummaryMetric[] => [
  {
    title: 'XKnow AI Independente',
    count: data?.xknowOptimizationAnalysis?.summary?.opportunities || 0,
    value: data?.xknowOptimizationAnalysis?.summary?.probable_monthly_savings || 0,
    subtitle: 'Análise própria por métricas e custos, executada em paralelo ao Azure.',
    accent: '#0891b2',
    icon: <DollarOutlined />,
    drilldownKey: 'xknowOptimization'
  },
  {
    title: 'Rightsizing de VMs',
    count: data?.executiveSummary?.idleResources?.total || 0,
    subtitle: 'Recursos com potencial de revisão ou descomissionamento.',
    accent: '#2563eb',
    icon: <CloudServerOutlined />
  },
  {
    title: 'Discos e Ociosidade',
    count: data?.executiveSummary?.idleResources?.disks || 0,
    subtitle: 'Managed disks e itens sem uso recorrente.',
    accent: '#f59e0b',
    icon: <DatabaseOutlined />
  },
  {
    title: 'Alertas de Segurança',
    count: data?.executiveSummary?.securityAlerts?.total || 0,
    subtitle: 'Reforcar postura operacional e compliance.',
    accent: '#ef4444',
    icon: <SafetyOutlined />
  }
];

const buildTabs = (data: any): TabSection[] => {
  const xknowOptimization = toArray(data?.xknowOptimizationAnalysis?.opportunities).map((item, index) => ({
    name: item.title || item.resource_id || `Oportunidade XKnow ${index + 1}`,
    type: item.category || 'Otimização',
    badge: `Confiança ${Math.round(Number(item.confidence || 0) * 100)}%`,
    badgeTone: normalizeStatusTone(item.confidence_label),
    description: [
      toArray(item.evidence).join(' • '),
      item.overlaps_provider ? 'Também recomendada pelo Azure; não somar novamente.' : 'Sem sobreposição oficial identificada.',
      item.next_action
    ].filter(Boolean).join(' '),
    meta: item.resource_id || 'Análise por serviço',
    cost: `${numberFormatter(Number(item.probable_monthly_savings || 0), 'azure')}/mês`,
    sourceLabel: 'XKnow AI independente',
    sourceTone: 'info' as const
  }));
  const vmDetails = toArray(data?.vmDetails).map((item, index) => ({
    name: item.name || `Virtual Machine ${index + 1}`,
    type: item.size || 'Virtual Machine',
    badge: item.status || 'Monitorar',
    badgeTone: normalizeStatusTone(item.status),
    description: item.recommendation || 'Sem recomendacao detalhada.',
    meta: [
      item.location,
      typeof item.cpuUtilization === 'number' ? `CPU media ${item.cpuUtilization.toFixed(1)}%` : undefined,
      typeof item.memoryUtilization === 'number' ? `Memoria media ${item.memoryUtilization.toFixed(1)}%` : undefined
    ].filter(Boolean).join(' • '),
    cost: item.monthlyCost
  }));

  const idleResources = toArray(data?.idleResourcesDetails).map((item, index) => ({
    name: item.name || `Managed Disk ${index + 1}`,
    type: item.type || 'Managed Disk',
    badge: 'Ocioso',
    badgeTone: 'danger' as const,
    description: `Recurso sem uso relevante. ${item.monthlyCost ? `Custo mensal ${item.monthlyCost}.` : ''}`.trim(),
    cost: item.monthlyCost
  }));

  const security = toArray(data?.securityDetails).map((item, index) => ({
    name: item.title || `Alerta ${index + 1}`,
    type: item.affected ? `${item.affected} recurso(s)` : 'Segurança',
    badge: item.severity || 'Média',
    badgeTone: normalizeStatusTone(item.severity),
    description: item.description || item.recommendation || 'Ajuste recomendado para postura de seguranca.'
  }));

  const prioritizedRisks = toArray(data?.riskPrioritization?.prioritized_findings).map((item, index) => ({
    name: item.title || `Risco priorizado ${index + 1}`,
    type: item.resource_type || 'Cloud Risk',
    badge: `${Math.round(item.risk_score || 0)}/100`,
    badgeTone: normalizeStatusTone(item.priority),
    description: [
      item.business_impact,
      item.why_now,
      item.recommendation ? `Ação: ${item.recommendation}` : undefined
    ].filter(Boolean).join(' '),
    meta: [
      item.priority ? `Prioridade ${item.priority}` : undefined,
      item.effort ? `Esforço ${item.effort}` : undefined,
      item.confidence ? `Confiança ${Math.round(item.confidence * 100)}%` : undefined
    ].filter(Boolean).join(' • '),
    sourceLabel: 'Cloud Risk Prioritization AI',
    sourceTone: 'success' as const
  }));
  const securityRiskAnalysis = buildSecurityRiskItems(data);

  const upsizing = toArray(data?.upsizingAnalysis?.recommendations).map((item, index) => ({
    name: item.resourceName || `Recurso ${index + 1}`,
    type: item.type || 'Capacity',
    badge: 'Upsizing',
    badgeTone: 'warning' as const,
    description: item.reason || 'Recurso requer capacidade adicional.',
    meta: [item.currentSku || item.currentTier || item.currentPlan, item.recommendedSku || item.recommendedTier || item.recommendedPlan]
      .filter(Boolean)
      .join(' -> '),
    cost: item.additionalCost ? numberFormatter(item.additionalCost, 'azure') : undefined
  }));

  return [
    {
      key: 'xknowOptimization',
      label: 'XKnow AI',
      subtitle: 'Recomendações independentes produzidas em paralelo às ferramentas nativas do Azure.',
      items: xknowOptimization
    },
    {
      key: 'security-ai',
      label: 'IA de Segurança',
      subtitle: buildSecurityRiskSubtitle(data),
      items: securityRiskAnalysis
    },
    {
      key: 'risk-ai',
      label: 'Priorização IA',
      subtitle: data?.riskPrioritization?.executive_summary || 'Riscos ordenados por impacto, exposição e esforço de correção.',
      items: prioritizedRisks
    },
    {
      key: 'compute',
      label: 'Máquinas Virtuais',
      subtitle: 'Rightsizing e utilização do ambiente de computação.',
      items: vmDetails
    },
    {
      key: 'storage',
      label: 'Discos e Storage',
      subtitle: 'Recursos ociosos e oportunidades de redução imediata.',
      items: idleResources
    },
    {
      key: 'security',
      label: 'Segurança',
      subtitle: 'Alertas e gaps de configuração prioritários.',
      items: security
    },
    {
      key: 'capacity',
      label: 'Upsizing',
      subtitle: 'Recursos que precisam de aumento de capacidade.',
      items: upsizing
    }
  ].filter(section => section.items.length > 0);
};

const buildInventoryRows = (data: any): InventoryRow[] => {
  const inventory = data?.inventory || {};
  return [
    { name: 'Virtual Machines', count: inventory.virtualMachines || 0 },
    { name: 'Virtual Machines (Databricks)', count: inventory.databricksManagedInstances || 0 },
    { name: 'Managed Disks', count: inventory.managedDisks || 0 },
    { name: 'Storage Accounts', count: inventory.storageAccounts || 0 },
    { name: 'Public IPs', count: inventory.publicIPs || 0 },
    { name: 'Network Interfaces', count: inventory.networkInterfaces || 0 }
  ];
};

export const createAzurePresentation = (data: any): ExecutivePresentation => ({
  provider: 'azure',
  title: 'Azure FinOps',
  providerLabel: 'Microsoft Azure',
  customerLabel: resolveCustomerLabel(data, data?.subscription?.name),
  customerDomain: data?.customerDomain || data?.metadata?.customerDomain,
  customerLogoUrl: data?.customerLogoUrl || data?.metadata?.customerLogoUrl,
  scopeLabel: data?.subscription?.name || 'Subscription selecionada',
  selectorLabel: 'Subscription Azure',
  selectorPlaceholder: 'Selecione uma subscription Azure',
  overview: {
    monthlyAverageCost: data?.overview?.monthlyAverageCost || 0,
    annualSavingsEstimate: data?.overview?.annualSavingsEstimate || 0,
    usageCost: data?.overview?.usageCost,
    creditCost: data?.overview?.creditCost,
    monthlyAverageCalculation: data?.overview?.monthlyAverageCalculation
  },
  executiveSummary: {
    netMonthlySavings: data?.executiveSummary?.netMonthlySavings || 0,
    idleTotal: data?.executiveSummary?.idleResources?.total || 0,
    securityTotal: data?.executiveSummary?.securityAlerts?.total || 0
  },
  summaryMetrics: buildSummaryMetrics(data),
  services: buildServices(data),
  inventoryRows: buildInventoryRows(data),
  tabs: buildTabs(data),
  rawData: data
});
